import { Form, Formik, FormikActions } from "formik";
import { inject, observer } from "mobx-react";
import React from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "recompose";
import * as Yup from "yup";

import { TextField } from "nvent-web/components/form/TextField";
import SubmitCancelButtons from "nvent-web/components/SubmitCancelButtons";
import { buildMessage } from "nvent-web/i18n/yup";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { UserStore } from "nvent-web/stores/User";
import { AddProductParams } from "nvent-web/types/AddProductParams";
import { CommissioningFormValues } from "nvent-web/types/CommissioningFormValues";

import style from "./CommissioningForm.module.scss";

interface CommissioningFormProps {
  onClose: () => void;
  onSubmit?: (values: CommissioningFormValues) => void;
}

interface CommissioningFormInnerProps
  extends CommissioningFormProps,
    RouteComponentProps<AddProductParams>,
    InjectedIntlProps {
  projects: ProjectsStore;
  user: UserStore;
}

const emailSchema = Yup.string().email();

class CommissioningFormInner extends React.Component<CommissioningFormInnerProps> {
  schema = Yup.object({
    installerEmail: emailSchema.required(),
    clientEmail: emailSchema,
    additionalEmails: Yup.string().test({
      name: "emails-list",
      message: buildMessage("error.string.emailsList"),
      test: (value?: string) =>
        typeof value === "undefined" || value.split(/\s*,\s*/g).every((part) => emailSchema.isValidSync(part)),
    }),
  });

  private get initialValues(): CommissioningFormValues {
    const {
      user,
      projects: { projectDetails },
    } = this.props;

    return {
      installerEmail: user.email,
      clientEmail: projectDetails ? projectDetails.clientEmail : "",
      additionalEmails: "",
    };
  }

  render() {
    const { projectDetails } = this.props.projects;
    const { intl } = this.props;
    const isFinished = projectDetails && projectDetails.finished;

    return (
      <Formik
        validationSchema={this.schema}
        isInitialValid={true}
        initialValues={this.initialValues}
        onSubmit={this.handleSubmit}
      >
        {({ isValid, isSubmitting }) => (
          <Form noValidate={true}>
            <h3 className={style.title}>
              <FormattedMessage id={isFinished ? "commissioning.finishedTitle" : "commissioning.title"} />
            </h3>

            {!isFinished && (
              <p className={style.notice}>
                <FormattedMessage id="commissioning.notice" />
              </p>
            )}

            <TextField
              type="email"
              name="installerEmail"
              label={intl.formatMessage({ id: "form.commissioning.installerEmail" })}
              placeholder={intl.formatMessage({ id: "form.commissioning.installerEmail" })}
              className={{ wrapper: style.row, label: style.label, inputContainer: style.inputContainer }}
            />

            <TextField
              type="email"
              name="clientEmail"
              label={intl.formatMessage({ id: "form.commissioning.clientEmail" })}
              placeholder={intl.formatMessage({ id: "form.commissioning.clientEmail" })}
              className={{ wrapper: style.row, label: style.label, inputContainer: style.inputContainer }}
            />

            <TextField
              component="textarea"
              name="additionalEmails"
              label={intl.formatMessage({ id: "form.commissioning.additionalEmails" })}
              placeholder={intl.formatMessage({ id: "form.commissioning.additionalEmails.placeholder" })}
              className={{ wrapper: style.row, label: style.label, inputContainer: style.inputContainer }}
            />

            <div className={style.actions}>
              <SubmitCancelButtons
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
                submitLabel={<FormattedMessage id={isFinished ? "actions.resend" : "actions.send"} />}
                onCancel={this.handleCancel}
              />
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  private handleCancel = () => {
    this.props.onClose();
  };

  private handleSubmit = async (values: CommissioningFormValues, actions: FormikActions<CommissioningFormValues>) => {
    const {
      onSubmit,
      projects: { sendCommission, projectDetails },
    } = this.props;

    try {
      await sendCommission(projectDetails!.id, values);

      if (onSubmit) {
        onSubmit(values);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };
}

const CommissioningForm = compose<CommissioningFormInnerProps, CommissioningFormProps>(
  injectIntl,
  inject("projects", "user"),
  withRouter,
  observer
)(CommissioningFormInner);

export default CommissioningForm;
